import { useQuery } from '@tanstack/react-query'

import { cbGetOfframpOptions } from '@src/pages/FiatExchange/api/coinbase.api'
import { DEFAULT_ONRAMP_TOKEN } from '@src/pages/FiatExchange/config'
import { CashOutMethod } from '@src/types/coinbase'
import { logger } from '@src/utils/logger'

/**
 * Hook to fetch available payment options for offramp transactions
 *
 * @param country - ISO country code (e.g., 'US')
 * @param subdivision - Optional subdivision code
 * @param cryptoAsset - Cryptocurrency asset code (e.g., 'ETH')
 * @param enabled - Whether the query should auto-execute
 * @param prefetch - Whether to fetch all cashout methods or only for a specific crypto asset
 */
export const useOfframpPaymentOptions = (
    country: string,
    subdivision?: string, // Used for some countries
    cryptoAsset?: string,
    enabled = true,
    prefetch = false
) => {
    // Use DEFAULT_ONRAMP_TOKEN if cryptoAsset is not provided but prefetch is true
    const effectiveAsset = prefetch && !cryptoAsset ? DEFAULT_ONRAMP_TOKEN.symbol : cryptoAsset

    return useQuery({
        // Include the effective asset in the query key for caching
        queryKey: prefetch
            ? ['offramp-payment-options', 'prefetch', country, effectiveAsset]
            : ['offramp-payment-options', country, effectiveAsset],
        enabled: enabled,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        queryFn: async () => {
            try {
                logger.info(`Fetching offramp payment options for country: ${country}, asset: ${cryptoAsset || 'any'}`)
                // The API doesn't accept asset directly, but we'll filter results if cryptoAsset is specified
                const response = await cbGetOfframpOptions(country, subdivision)
                logger.debug('cbGetOfframpOptions response:', response)

                // Initialize empty cashout methods array
                let cashoutMethods: CashOutMethod[] = []

                // Handle the API response to extract cashout methods
                if (response && response.cashout_currencies) {
                    // Use type assertion to safely access the cashout currencies data
                    const currencies = response.cashout_currencies as Record<string, any>

                    // If we have an effective asset (either provided or default), use it directly
                    if (effectiveAsset && currencies[effectiveAsset]) {
                        const assetData = currencies[effectiveAsset]
                        if (assetData?.limits) {
                            const limits = assetData.limits as Array<{ id: string; min: string; max: string }>

                            // Create cashout method objects from the limits
                            cashoutMethods = limits.map(limit => ({
                                id: limit.id,
                                description: `Cash out using ${limit.id.split('_').join(' ').toLowerCase()}`,
                                limits: [
                                    {
                                        id: limit.id,
                                        min: limit.min,
                                        max: limit.max,
                                        currency: effectiveAsset,
                                    },
                                ],
                            }))
                        }
                    }
                    // If no effective asset or it's not found, fall back to collecting all methods
                    else if (prefetch) {
                        // Collect unique cashout methods across all currencies
                        const uniqueMethods = new Map<string, CashOutMethod>()

                        Object.entries(currencies).forEach(([currencyCode, currencyData]: [string, any]) => {
                            if (currencyData?.limits) {
                                const limits = currencyData.limits as Array<{ id: string; min: string; max: string }>

                                limits.forEach(limit => {
                                    // Only add if not already added (prioritize first occurrence)
                                    if (!uniqueMethods.has(limit.id)) {
                                        uniqueMethods.set(limit.id, {
                                            id: limit.id,
                                            description: `Cash out using ${limit.id.split('_').join(' ').toLowerCase()}`,
                                            limits: [
                                                {
                                                    id: limit.id,
                                                    min: limit.min,
                                                    max: limit.max,
                                                    currency: currencyCode,
                                                },
                                            ],
                                        })
                                    }
                                })
                            }
                        })

                        cashoutMethods = Array.from(uniqueMethods.values())
                    }
                    // Asset-specific mode for when no default was provided
                    else if (!effectiveAsset) {
                        // Find the first available asset if none was specified
                        const currencyKey = Object.keys(currencies)[0]

                        // Extract available cashout methods for this currency
                        if (currencyKey && currencies[currencyKey]?.limits) {
                            const currencyData = currencies[currencyKey]
                            const limits = currencyData.limits as Array<{ id: string; min: string; max: string }>

                            // Create cashout method objects from the limits
                            cashoutMethods = limits.map(limit => ({
                                id: limit.id,
                                description: `Cash out using ${limit.id.split('_').join(' ').toLowerCase()}`,
                                limits: [
                                    {
                                        id: limit.id,
                                        min: limit.min,
                                        max: limit.max,
                                        currency: currencyKey,
                                    },
                                ],
                            }))
                        }
                    }
                }

                logger.debug('Offramp cashout methods:', cashoutMethods)
                return cashoutMethods
            } catch (error) {
                logger.error('Error fetching offramp payment options:', error)
                return []
            }
        },
    })
}
